import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Task } from "src/api/types";
import { useIsMobile } from "src/hooks/useIsMobile";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useTranslation } from "react-i18next";
import TaskModal from "src/shared/components/TaskModal";

interface Props {
  item: Task;
}

const ActionItem = ({ item }: Props) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState<boolean>(false);

  const title = useMemo(
    () => t(`tasks.${item.task_type.toLowerCase()}.title`),
    [item.task_type, t],
  );

  const description = useMemo(
    () => t(`tasks.${item.task_type.toLowerCase()}.description`),
    [item.task_type, t],
  );

  const caption = useMemo(() => {
    if (item.task_type !== "DISCOUNT_OFFER") {
      return null;
    }

    return t(`tasks.${item.task_type.toLowerCase()}.caption`, "Discount Alert");
  }, [item.task_type, t]);

  const handleClick = useCallback(() => {
    if (item.task_link) {
      navigate(item.task_link);
    }

    if (item.task_modal) {
      setOpenModal((o) => !o);
    }
  }, [item, navigate]);

  const displayIcon = useMemo(
    () => !["DISCOUNT_OFFER", "WL_OPTIONS"].includes(item.task_type),
    [item.task_type],
  );

  return (
    <Stack direction="row">
      <Stack gap={1}>
        <Stack direction="row" alignItems="center" gap={1}>
          {displayIcon ? (
            <NewReleasesIcon
              color="warning"
              sx={{ alignSelf: isMobile ? "start" : "center" }}
            />
          ) : null}
          {caption ? (
            <Typography variant="body2" fontWeight={600}>
              {caption}
            </Typography>
          ) : null}
        </Stack>
        <Typography fontWeight={isMobile ? 500 : 600}>{title}</Typography>
        {isMobile ? null : <Typography>{description}</Typography>}
      </Stack>
      <IconButton
        sx={{ marginLeft: "auto", alignSelf: "center" }}
        onClick={handleClick}
      >
        <NavigateNextIcon sx={{ color: "#00B6B9" }} fontSize="medium" />
      </IconButton>
      {item.task_modal ? (
        <TaskModal open={openModal} onClose={setOpenModal} task={item} />
      ) : null}
    </Stack>
  );
};

export default ActionItem;
